import {
  ApolloClient,
  ApolloProvider,
  NormalizedCacheObject,
} from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import { Suspense } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import 'supertokens-auth-react';
import { ActorRefFrom } from 'xstate';

import { SessionMachine } from '@eluve/apollo-client';
import {
  SingularEntityRedirect,
  TenantToOrganizationRedirect,
} from '@eluve/app-router-utils';
import { TableSkeleton } from '@eluve/blocks';
import { LoadingIndicator, Stack, Text } from '@eluve/components';
import { ExperimentPage, ExperimentsListPage } from '@eluve/experiments-admin';
import {
  GlobalAssetDeletionHistoryPage,
  SubmitAssetDeletionRequestPage,
  TenantAssetDeletionHistoryPage,
} from '@eluve/frontend-asset-deletion-dash';
import {
  GlobalConversationStarterDefaultsPage,
  TenantConversationStarterDefaultsPage,
} from '@eluve/frontend-feature-conversation-starter-defaults';
import {
  ArtifactDetailsPage,
  ArtifactListPage,
  CreateArtifactPage,
  CreateDatasetPage,
  CreateWorkflowPage,
  DatasetDetailsPage,
  DatasetsListPage,
  DuplicateWorkflowContextProvider,
  FactVerificationIndexPage,
  FactVerificationModelPromptTemplatePage,
  FactVerificationWorkflowDetailsPage,
} from '@eluve/frontend-feature-eval';
import {
  OutputTemplatesListPage,
  outputTemplateRoutes,
} from '@eluve/frontend-feature-output-templates';
import {
  GlobalPromptDefaultsTab,
  TenantPromptDefaultsTab,
} from '@eluve/frontend-feature-prompt-template-defaults';
import {
  CreatePromptTemplatePage,
  CreatePromptTemplateVariantPage,
  ModelPromptTemplateDetailsPage,
  ModelsListPage,
  PromptTemplateDetailsLayout,
  PromptTemplateDetailsPromptTextPage,
  PromptTemplateModelArgsListPage,
  PromptTemplateUsersPage,
  PromptTemplatesListPage,
} from '@eluve/frontend-feature-prompt-templates';
import {
  ReviewAppointmentLayout,
  reviewAppointmentDetailsRoutes,
} from '@eluve/frontend-review-dash';
import { SiteBannersListPage } from '@eluve/site-banners';

import { AdminAuditTrail } from './AdminAuditTrail';
import { AdminLayout } from './AdminLayout';
import { TenantGuard } from './TenantGuard';
import { ApiTokensPage } from './api-tokens/ApiTokensPage';
import { BillingCodesPage } from './billing-codes/BillingCodesPage';
import { MlEngineersListPage } from './components/MlEngineersListPage';
import { ReviewersListPage } from './components/ReviewersListPage';
import { GlobalFeatureFlagsPage } from './global-features-flags/GlobalFeatureFlagsPage';
import { MedplumDiagnosticsPage } from './medplum/MedplumDiagnosticsPage';
import { MedplumTenantPage } from './medplum/MedplumTenantPage';
import { OnCallPage } from './on-call/OnCallPage';
import { CreatePatientImportPage } from './patient-imports/CreatePatientImportPage';
import { PatientImportDetailPage } from './patient-imports/PatientImportDetailPage';
import { PatientImportFinalizePage } from './patient-imports/PatientImportFinalizePage';
import { PatientImportsListPage } from './patient-imports/PatientImportsListPage';
import {
  CreateTemplateBundlePage,
  TemplateBundleDetailPage,
  TemplateBundlesListPage,
} from './template-bundles';
import { AdminTenant } from './tenant/$tenantId/AdminTenant';
import { AdminTenantBundleDetailPage } from './tenant/$tenantId/AdminTenantBundleDetailPage';
import { AdminTenantCodeTypesPage } from './tenant/$tenantId/AdminTenantCodeTypesPage';
import { AdminTenantFeatureFlagsPage } from './tenant/$tenantId/AdminTenantFeatureFlagsPage';
import { AdminTenantImportedOutputTemplatesPage } from './tenant/$tenantId/AdminTenantImportedOutputTemplatesPage';
import { AdminTenantLayout } from './tenant/$tenantId/AdminTenantLayout';
import { AdminTenantLlmOutputTemplatesPage } from './tenant/$tenantId/AdminTenantLlmOutputTemplatesPage';
import { AdminTenantLlmOutputsPage } from './tenant/$tenantId/AdminTenantLlmOutputsPage';
import { AdminTenantMlEngineersPage } from './tenant/$tenantId/AdminTenantMlEngineersPage';
import { AdminTenantPromptTemplatesPage } from './tenant/$tenantId/AdminTenantPromptTemplatesPage';
import { AdminTenantReviewersPage } from './tenant/$tenantId/AdminTenantReviewersPage';
import { AdminTenantTemplateBundlesPage } from './tenant/$tenantId/AdminTenantTemplateBundlesPage';
import { AdminTenantUserDefaultSettingsPage } from './tenant/$tenantId/AdminTenantUserDefaultSettingsPage';
import { AssetDeletionDash } from './tenant/$tenantId/asset-deletion/AssetDeletionDash';
import { TenantDash } from './tenant/$tenantId/dash/TenantDash';
import { AdminAppointment } from './tenant/$tenantId/dash/appointments/$appointmentId/AdminAppointment';
import { AdminAppointmentOutlet } from './tenant/$tenantId/dash/appointments/$appointmentId/AdminAppointmentOutlet';
import { AdminAppointmentFiles } from './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminAppointmentFiles';
import { AdminAppointmentSourceArtifacts } from './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminAppointmentSourceArtifacts';
import { AdminSummaryContent } from './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminSummaryContent';
import { AdminTranscriptsPage } from './tenant/$tenantId/dash/appointments/$appointmentId/components/AdminTranscriptsPage';
import { AdminAppointmentsDash } from './tenant/$tenantId/dash/appointments/AdminAppointmentsDash';
import { SwapAppointmentsPage } from './tenant/$tenantId/swap-appointments/SwapAppointmentsPage';
import { AdminTenantUsers } from './tenant/$tenantId/users/AdminTenantUsers';
import { AdminTenants } from './tenant/AdminTenants';
import { AdminTestEmailPage } from './test-email/AdminTestEmailPage';
import { UserPage } from './users/UserPage';
import { UsersTable } from './users/UsersTable';

export const createAdminRoutes = ({
  path,
  client,
  tenantScopedApolloClient,
  sessionActor,
  postHogProjectId,
  langfuseBaseUrl,
  langfuseProjectId,
}: {
  path: string;
  client: ApolloClient<NormalizedCacheObject>;
  tenantScopedApolloClient: ApolloClient<NormalizedCacheObject>;
  sessionActor: ActorRefFrom<SessionMachine>;
  postHogProjectId: string;
  langfuseBaseUrl?: string | null;
  langfuseProjectId?: string | null;
}) => {
  return [
    {
      path,
      element: <AdminLayout />,
      children: [
        {
          index: true,
          element: <Navigate to="organizations" replace={true} />,
        },
        {
          path: 'tenant/*',
          element: <TenantToOrganizationRedirect />,
        },
        {
          path: 'tenants/*',
          element: <TenantToOrganizationRedirect />,
        },
        {
          path: 'organizations',
          element: (
            <ApolloProvider client={client}>
              <AdminTenants />
            </ApolloProvider>
          ),
        },
        {
          path: 'audit-trail',
          element: (
            <ApolloProvider client={client}>
              <AdminAuditTrail />
            </ApolloProvider>
          ),
        },
        {
          path: 'fact-verification',
          element: (
            <ApolloProvider client={client}>
              <Outlet />
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <FactVerificationIndexPage />,
            },
            {
              path: 'workflow/create',
              element: <CreateWorkflowPage />,
            },
            {
              path: 'workflow/copy/:workflowId',
              element: (
                <DuplicateWorkflowContextProvider>
                  <CreateWorkflowPage />
                </DuplicateWorkflowContextProvider>
              ),
            },
            {
              path: ':workflowId',
              element: <FactVerificationWorkflowDetailsPage />,
            },
            {
              path: ':workflowId/:modelPromptTemplateId',
              element: <FactVerificationModelPromptTemplatePage />,
            },
            {
              path: 'artifacts',
              element: <ArtifactListPage />,
            },
            {
              path: 'artifacts/:artifactId',
              element: <ArtifactDetailsPage />,
            },
            {
              path: 'artifacts/create',
              element: <CreateArtifactPage />,
            },
            {
              path: 'datasets',
              element: <DatasetsListPage />,
            },
            {
              path: 'datasets/:datasetId',
              element: <DatasetDetailsPage />,
            },
            {
              path: 'datasets/create',
              element: <CreateDatasetPage />,
            },
          ],
        },
        {
          path: 'models',
          element: (
            <ApolloProvider client={client}>
              <ModelsListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'template-bundles',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <TemplateBundlesListPage />,
            },
            {
              path: 'create',
              element: <CreateTemplateBundlePage />,
            },
            {
              path: ':bundleId',
              element: <TemplateBundleDetailPage />,
            },
          ],
        },
        {
          path: 'output-templates',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <OutputTemplatesListPage />,
            },
            ...outputTemplateRoutes,
          ],
        },
        {
          path: 'prompts',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <PromptTemplatesListPage />,
            },
            {
              path: 'create',
              element: (
                <CreatePromptTemplatePage onDoneNavigateTo="/admin/prompts" />
              ),
            },
            {
              path: 'clone/:promptTemplateId',
              element: (
                <CreatePromptTemplatePage onDoneNavigateTo="/admin/prompts" />
              ),
            },
            {
              path: 'clone/:promptTemplateId/variant/:variantNumber',
              element: <CreatePromptTemplateVariantPage />,
            },
            {
              path: ':promptTemplateId',
              element: <PromptTemplateDetailsLayout />,
              children: [
                {
                  index: true,
                  element: <Navigate to="prompt" replace />,
                },
                {
                  path: 'prompt',
                  element: <PromptTemplateDetailsPromptTextPage />,
                },
                {
                  path: 'prompt/variant/:variantNumber',
                  element: <PromptTemplateDetailsPromptTextPage />,
                },
                {
                  path: 'model-args',
                  element: <PromptTemplateModelArgsListPage />,
                },
                {
                  path: 'users',
                  element: <PromptTemplateUsersPage />,
                },
              ],
            },
            {
              path: ':promptTemplateId/:modelPromptTemplateId',
              element: <ModelPromptTemplateDetailsPage />,
            },
            {
              path: 'global-defaults',
              element: <GlobalPromptDefaultsTab />,
            },
          ],
        },
        {
          path: 'conversation-starters',
          element: (
            <ApolloProvider client={client}>
              <GlobalConversationStarterDefaultsPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'organization/*',
          element: <SingularEntityRedirect entityName="organization" />,
        },
        {
          path: 'organizations/:tenantId',
          element: (
            <TenantGuard
              client={tenantScopedApolloClient}
              sessionActor={sessionActor}
            >
              <AdminTenantLayout />
            </TenantGuard>
          ),
          children: [
            {
              index: true,
              element: <Navigate to="manage" replace />,
            },
            {
              path: 'manage',
              element: (
                <ApolloProvider client={client}>
                  <AdminTenant />
                </ApolloProvider>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="feature-flags" replace />,
                },
                {
                  path: 'feature-flags',
                  element: <AdminTenantFeatureFlagsPage />,
                },
                {
                  path: 'llm-outputs',
                  element: <AdminTenantLlmOutputsPage />,
                },
                {
                  path: 'prompt-templates',
                  element: <AdminTenantPromptTemplatesPage />,
                },
                {
                  path: 'llm-output-templates',
                  element: <AdminTenantLlmOutputTemplatesPage />,
                },
                {
                  path: 'imported-llm-output-templates',
                  element: <AdminTenantImportedOutputTemplatesPage />,
                },
                {
                  path: 'default-user-settings',
                  element: <AdminTenantUserDefaultSettingsPage />,
                },
                {
                  path: 'code-types',
                  element: <AdminTenantCodeTypesPage />,
                },
                {
                  path: 'reviewers',
                  element: <AdminTenantReviewersPage />,
                },
                {
                  path: 'ml-engineers',
                  element: <AdminTenantMlEngineersPage />,
                },
                {
                  path: 'prompt-defaults',
                  element: <TenantPromptDefaultsTab />,
                },
                {
                  path: 'template-bundles',
                  children: [
                    {
                      index: true,
                      element: <AdminTenantTemplateBundlesPage />,
                    },
                    {
                      path: ':bundleId',
                      element: <AdminTenantBundleDetailPage />,
                    },
                  ],
                },
                {
                  path: 'conversation-starters',
                  element: <TenantConversationStarterDefaultsPage />,
                },
              ],
            },
            {
              path: 'dash',
              element: <TenantDash />,
            },
            {
              path: 'asset-deletion',
              element: (
                <ApolloProvider client={client}>
                  <AssetDeletionDash />
                </ApolloProvider>
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="new" replace />,
                },
                {
                  path: 'new',
                  element: <SubmitAssetDeletionRequestPage />,
                },
                {
                  path: 'history',
                  element: <TenantAssetDeletionHistoryPage />,
                },
              ],
            },
            {
              path: 'appointment/*',
              element: <SingularEntityRedirect entityName="appointment" />,
            },
            {
              path: 'appointments',
              element: <AdminAppointmentsDash />,
            },
            {
              path: 'appointments/:appointmentId',
              element: (
                <AdminAppointmentOutlet
                  langfuseBaseUrl={langfuseBaseUrl}
                  langfuseProjectId={langfuseProjectId}
                />
              ),
              children: [
                {
                  index: true,
                  element: <Navigate to="details" replace />,
                },
                {
                  path: 'details',
                  element: (
                    <ErrorBoundary
                      fallback={
                        <div>
                          Failed to load appointment details. Please check your
                          permissions or user role.
                        </div>
                      }
                    >
                      <AdminAppointment />
                    </ErrorBoundary>
                  ),
                },
                {
                  path: 'artifacts',
                  element: <ReviewAppointmentLayout />,
                  // Admin dashboard overrides only the `summary` sub-tab with
                  // AdminSummaryContent (version history + generation metadata
                  // + failure reasons — admin-debugging concerns). Reviewer
                  // tools keep the shared SummaryContent via
                  // reviewAppointmentDetailsRoutes.
                  children: [
                    ...reviewAppointmentDetailsRoutes.filter(
                      (r) => r.path !== 'summary',
                    ),
                    { path: 'summary', element: <AdminSummaryContent /> },
                  ],
                },
                {
                  path: 'files',
                  element: (
                    <Suspense fallback={<TableSkeleton rowCount={10} />}>
                      <ErrorBoundary
                        fallback={
                          <Stack gap={2}>
                            <Text size="md">
                              Failed to load appointment files
                            </Text>
                            <TableSkeleton rowCount={5} />
                          </Stack>
                        }
                      >
                        <AdminAppointmentFiles />
                      </ErrorBoundary>
                    </Suspense>
                  ),
                },
                {
                  path: 'transcript-comparison',
                  element: <AdminTranscriptsPage />,
                },
                {
                  path: 'source-artifacts',
                  element: (
                    <ApolloProvider client={client}>
                      <AdminAppointmentSourceArtifacts />
                    </ApolloProvider>
                  ),
                },
              ],
            },
            {
              path: 'users',
              element: <AdminTenantUsers />,
            },
            {
              // Deliberately unlinked from any nav — reachable only by URL.
              path: 'swap-appointments',
              element: <SwapAppointmentsPage />,
            },
          ],
        },

        {
          path: 'global-feature-flags',
          element: (
            <ApolloProvider client={client}>
              <GlobalFeatureFlagsPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'users',
          element: (
            <ApolloProvider client={client}>
              <Outlet />
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <UsersTable />,
            },
            {
              path: ':userId',
              element: <UserPage postHogProjectId={postHogProjectId} />,
            },
          ],
        },
        {
          path: 'api-tokens',
          element: (
            <ApolloProvider client={client}>
              <ApiTokensPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports',
          element: (
            <ApolloProvider client={client}>
              <PatientImportsListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/new',
          element: (
            <ApolloProvider client={client}>
              <CreatePatientImportPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/:jobId/finalize',
          element: (
            <ApolloProvider client={client}>
              <PatientImportFinalizePage />
            </ApolloProvider>
          ),
        },
        {
          path: 'patient-imports/:jobId',
          element: (
            <ApolloProvider client={client}>
              <PatientImportDetailPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'reviewers-list',
          element: (
            <ApolloProvider client={client}>
              <ReviewersListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'ml-engineers-list',
          element: (
            <ApolloProvider client={client}>
              <MlEngineersListPage />
            </ApolloProvider>
          ),
        },

        {
          path: 'site-banners',
          element: (
            <ApolloProvider client={client}>
              <SiteBannersListPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'experiments',
          element: (
            <ApolloProvider client={client}>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </ApolloProvider>
          ),
          children: [
            {
              index: true,
              element: <ExperimentsListPage />,
            },
            {
              path: ':experimentId',
              element: <ExperimentPage />,
            },
          ],
        },
        {
          path: 'asset-deletion-global-history',
          element: (
            <ApolloProvider client={client}>
              <GlobalAssetDeletionHistoryPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'on-call',
          element: (
            <OnCallPage
              langfuseBaseUrl={langfuseBaseUrl}
              langfuseProjectId={langfuseProjectId}
            />
          ),
        },
        {
          path: 'billing-codes',
          element: (
            <ApolloProvider client={client}>
              <BillingCodesPage />
            </ApolloProvider>
          ),
        },
        {
          path: 'medplum',
          element: <MedplumDiagnosticsPage />,
        },
        {
          path: 'medplum/tenants/:tenantId',
          element: <MedplumTenantPage />,
        },
        {
          path: 'test-email',
          element: <AdminTestEmailPage />,
        },
      ],
    },
  ];
};
